import { useIntl } from 'react-intl';
import { isEmpty, isNil } from 'lodash';
import { Box, Typography } from '@material-ui/core';
import React, { FC, useContext } from 'react';
import { useFormState } from 'react-final-form';
import { useShopConfig } from '../../hooks/shopConfig';
import Heading from './Heading';
import { getTradeInCreditPriceOptions, shouldDisableTradeIn } from './EditListingPage.utils';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { Conditions } from '../../shopConfig/filters/condition';
import {
  convertNumberToMoney,
  formatDollarAmount,
  formatPayoutPercentage,
} from '../../util/currency';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import {
  ConditionalWrapper,
  FieldRadioButton,
  InlineTextButton,
  TypographyWrapper,
  ValidationError,
} from '../../components';
import AppContext from '../../context/AppContext';
import FormOptionsTable from '../../components/FormOptionsTable/FormOptionsTable';
import { ListingItemType } from '../../types/sharetribe/listing';
import BackToPreviousTabLink from './BackToPreviousTabLink';
import { useOwnListingVersions } from './hooks/useOwnListingVersions';
import { useTradeInConfig } from '../../hooks/useTradeInConfig';
import { getShopifyProductVariantCompareAtAndPriceAverage } from '../../util/listings/listing';
import { ITradeInPriceRule } from '../../types/contentful/types.generated';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';
import { ListingFlowFormField } from './ListingFlowTypes';
import { getRecommendedPriceForCondition } from '../../util/listingPrice';

const ListForSaleOption: FC = () => {
  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();
  const { conditionToRecommendedDiscount, pricingRestrictions } = useListingFlowConfig();
  const { treetShopName, creditPayoutPercentage, resaleValueConfig } = useShopConfig();
  const formattedCreditPayout = formatPayoutPercentage(creditPayoutPercentage);
  const { values } = useFormState();
  const { category, originalPrice, shopifyProductVariant, tags } = values;

  let recommendedPriceOptions: number[] | null = null;
  if (originalPrice) {
    const shopifyVariantCompareAtPriceAndPriceAverage =
      getShopifyProductVariantCompareAtAndPriceAverage(shopifyProductVariant);

    const priceToUse =
      resaleValueConfig && shopifyVariantCompareAtPriceAndPriceAverage
        ? convertNumberToMoney(shopifyVariantCompareAtPriceAndPriceAverage, currencyConfig.currency)
        : originalPrice;

    recommendedPriceOptions = Object.keys(conditionToRecommendedDiscount).map((condition) =>
      getRecommendedPriceForCondition({
        price: priceToUse,
        condition: condition as Conditions,
        conditionToRecommendedDiscount,
        pricingRestrictions,
        tags,
        category,
        resaleValueConfig,
      })
    );
  }
  return (
    <>
      <Typography variant="body1" style={{ fontWeight: 'bold', display: 'inline' }}>
        Sell
      </Typography>{' '}
      {recommendedPriceOptions && (
        <Typography variant="body1" style={{ display: 'inline' }}>
          {`(Recommended Sale Price: ${intl.formatNumber(
            Math.min(...recommendedPriceOptions),
            currencyConfig
          )} - ${intl.formatNumber(Math.max(...recommendedPriceOptions), currencyConfig)})`}
        </Typography>
      )}
      <Typography variant="body2">
        List for sale on {treetShopName} and{' '}
        {creditPayoutPercentage
          ? `get ${formattedCreditPayout}% brand credit when your item sells.`
          : 'earn maximum cash payouts when your item sells.'}
      </Typography>
    </>
  );
};

const TradeInOption: FC = () => {
  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();
  const { values } = useFormState();
  const { shopifyProductVariant, category } = values;
  const { treetId } = useContext(AppContext);
  const { additionalPayoutSettings, listingFlowConfig } = useShopConfig();
  const { currentListing } = useOwnListingVersions();
  const tradeInConfig = useTradeInConfig();
  const { tradeInAllowedFindItemMethods } = tradeInConfig;

  const { publicData } = currentListing.attributes;
  const { currency } = currencyConfig;
  const { findItemMethod } = publicData;
  const isTradeInDisabled = shouldDisableTradeIn(tradeInAllowedFindItemMethods, findItemMethod);

  const tradeInPriceRules = tradeInConfig?.tradeInPriceRulesCollection?.items || [];

  const { conditionToRecommendedDiscount, originalPriceSources } = listingFlowConfig;

  const tradeInCreditPriceOptions = getTradeInCreditPriceOptions({
    treetId,
    shopifyProduct: publicData?.shopifyProduct,
    shopifyProductVariant,
    currency,
    isPaidOutOnBundle: !isNil(additionalPayoutSettings?.tradeInCreditPayoutBundleAmount),
    conditionToRecommendedDiscount,
    category,
    tradeInPriceRules,
    originalPriceSources,
    findItemMethod,
  });

  let tradeInPriceDisplay = '';
  if (!isEmpty(tradeInCreditPriceOptions)) {
    const maxTradeInCreditPrice = Math.max(...tradeInCreditPriceOptions);
    const minTradeInCreditPrice = Math.min(...tradeInCreditPriceOptions);
    const displaySinglePrice = minTradeInCreditPrice === maxTradeInCreditPrice;

    const bundlePriceRule = tradeInPriceRules.find(
      (rule: ITradeInPriceRule) => rule.target === 'BUNDLE' && rule.fixedAmount !== undefined
    );

    if (bundlePriceRule) {
      tradeInPriceDisplay = `(${formatDollarAmount(
        intl,
        bundlePriceRule.fixedAmount,
        currencyConfig
      )} in Brand Credit For Entire Shipment)`;
    } else if (displaySinglePrice) {
      tradeInPriceDisplay = `(${formatDollarAmount(
        intl,
        maxTradeInCreditPrice,
        currencyConfig
      )} in Brand Credit)`;
    } else {
      tradeInPriceDisplay = `(${formatDollarAmount(
        intl,
        minTradeInCreditPrice,
        currencyConfig
      )} - ${formatDollarAmount(intl, maxTradeInCreditPrice, currencyConfig)} in Brand Credit)`;
    }
  }

  return (
    <>
      <Typography variant="body1" style={{ fontWeight: 'bold', display: 'inline' }}>
        Trade In{isTradeInDisabled ? '*' : ''}
      </Typography>{' '}
      <Typography variant="body1" style={{ display: 'inline' }}>
        {tradeInPriceDisplay}
      </Typography>
      <Typography variant="body2">
        Send in your items and get paid as soon as they’re verified.
      </Typography>
    </>
  );
};

const ITEM_TYPE_CONFIG = {
  [ListingItemType.Marketplace]: {
    label: <ListForSaleOption />,
  },
  [ListingItemType.TradeIn]: {
    label: <TradeInOption />,
  },
};

const ItemType: FC = () => {
  const { shopName } = useShopConfig();
  const { tradeInAllowedFindItemMethods } = useTradeInConfig();
  const { errors, touched } = useFormState();
  const fieldError = errors?.[ListingFlowFormField.ItemType];
  const fieldTouched = touched?.[ListingFlowFormField.ItemType];

  const { currentListing } = useOwnListingVersions();

  const { publicData } = currentListing.attributes;
  const isTradeInDisabled = shouldDisableTradeIn(
    tradeInAllowedFindItemMethods,
    publicData?.findItemMethod
  );

  return (
    <>
      <Heading style={{ paddingTop: 0, paddingBottom: '16px' }}>Sell Or Trade In</Heading>
      <ValidationError fieldMeta={{ touched: fieldTouched, error: fieldError }} />
      <Box mb={6}>
        <FormOptionsTable containerId={ListingFlowFormField.ItemType}>
          {Object.entries(ITEM_TYPE_CONFIG).map(([itemType, optionConfig]) => {
            const { label } = optionConfig;

            const isDisabled = itemType === ListingItemType.TradeIn && isTradeInDisabled;

            const styledLabel = (
              <ConditionalWrapper
                condition={isDisabled}
                wrapper={(children) => (
                  <Box style={{ opacity: 0.5, cursor: 'not-allowed' }}>{children}</Box>
                )}
              >
                {label}
              </ConditionalWrapper>
            );

            return (
              <Box py={1} key={`${ListingFlowFormField.ItemType}-${itemType}`}>
                <FieldRadioButton
                  key={`${ListingFlowFormField.ItemType}-${itemType}`}
                  showAsRequired
                  id={itemType}
                  name={ListingFlowFormField.ItemType}
                  label={styledLabel}
                  value={itemType}
                  disabled={isDisabled}
                />
              </Box>
            );
          })}
        </FormOptionsTable>
        {isTradeInDisabled && (
          <TypographyWrapper
            variant="body1"
            typographyOverrides={{ style: { paddingTop: '16px', fontWeight: 'bold' } }}
          >
            *Trade-in is only available for items in {shopName}’s item catalogue.
            <BackToPreviousTabLink style={{ display: 'inline', paddingLeft: '8px' }}>
              <InlineTextButton>
                <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
                  Change Item
                </TypographyWrapper>
              </InlineTextButton>
            </BackToPreviousTabLink>
          </TypographyWrapper>
        )}
      </Box>
    </>
  );
};

export default ItemType;
